import {
  FILE_TYPE,
  fileType,
  IMediaDraft,
  IMediaFile,
} from "@home-health-notify/domain";
import { Box, IconButton, Typography } from "@mui/material";
import { useTranslation } from "hhn-commons";
import React, { useEffect, useState } from "react";
import { getFileParams } from "./FileUtils";
import {
  CAPTION_INPUT_BOTTOM_MARGIN,
  CAPTION_INPUT_HEIGHT,
  CAPTION_INPUT_TOP_MARGIN,
  CaptionInput,
  CloseIconButton,
  DocImg,
  PreviewAudio,
  PreviewBodyContainer,
  PreviewDoc,
  PreviewHeaderContainer,
  PreviewImage,
  PreviewLoaderContainer,
  PreviewVideo,
} from "./Preview.style";

export const PreviewHeader = ({
  closePreview,
}: {
  closePreview: () => void;
}) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });
  return (
    <PreviewHeaderContainer>
      <IconButton onClick={closePreview}>
        <CloseIconButton fontSize={"medium"} />
      </IconButton>
      <Typography variant={"h6"} sx={{
        color: "#fff"
      }}>
        {tNs("preview.previewLabel")}
      </Typography>
    </PreviewHeaderContainer>
  );
};

export const PreviewLoader = () => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });
  return (
    <PreviewLoaderContainer>
      <Typography variant="h4" style={{ color: "rgba(0, 0, 0, 0.6)" }}>
        {tNs("preview.dropFiles")}
      </Typography>
    </PreviewLoaderContainer>
  );
};

export const PreviewBody = ({
  messageDraft,
  onSetCaption,
}: {
  messageDraft: IMediaDraft;
  onSetCaption: (mediaDraft: IMediaDraft, caption: string) => void;
}) => {
  const { t } = useTranslation();
  const [caption, setCaption] = useState("");

  useEffect(() => {
    setCaption(messageDraft.text || defaultCaption());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageDraft]);

  const defaultCaption = () => {
    if (messageDraft.mediaFile.isDoc()) {
      onSetCaption(messageDraft, messageDraft.mediaFile.name);
      return messageDraft.mediaFile.name;
    } else return "";
  };
  const tNs = (key: string) => t(key, { ns: "message" });
  return (
    <PreviewBodyContainer>
      <div style={{ display: "flex" }}></div>
      <PreviewComponent
        file={messageDraft.mediaFile}
        siblingsHeight={
          CAPTION_INPUT_BOTTOM_MARGIN +
          CAPTION_INPUT_TOP_MARGIN +
          CAPTION_INPUT_HEIGHT
        }
      />
      {fileType(messageDraft.mediaFile) !== FILE_TYPE.AUDIO && (
        <CaptionInput
          slotProps={{
            htmlInput: { "data-testid": "caption-text" },
          }}
          value={caption}
          label={tNs("preview.addCaption")}
          variant="standard"
          onChange={e => {
            let captionText = e.target.value;
            setCaption(captionText);
            messageDraft && onSetCaption(messageDraft, captionText);
          }}
        />
      )}
    </PreviewBodyContainer>
  );
};

export type IFileTypeComponentMap = {
  [key in FILE_TYPE]: React.ElementType;
};

const PreviewComponent = ({
  file,
  siblingsHeight,
}: {
  file: IMediaFile;
  siblingsHeight: number;
}) => {
  const FileTypeComponentMap: IFileTypeComponentMap = {
    [FILE_TYPE.IMAGE]: PreviewImageComponent,
    [FILE_TYPE.VIDEO]: PreviewVideoComponent,
    [FILE_TYPE.AUDIO]: PreviewAudioComponent,
    [FILE_TYPE.DOC]: PreviewDocumentComponent,
  };

  const fileParams = getFileParams(file);
  let FileTypeComponent = FileTypeComponentMap[fileParams.fileType];

  return (
    <FileTypeComponent
      iconPath={fileParams.iconPath}
      fileName={fileParams.fileName}
      fileSize={fileParams.fileSize}
      preview={fileParams.filePath}
      siblingsHeight={siblingsHeight}
    />
  );
};

const PreviewImageComponent = ({
  preview,
  siblingsHeight,
}: {
  preview: string;
  siblingsHeight: number;
}) => {
  const [dimensions, setDimensions] = useState<{
    width?: number;
    height?: number;
  }>({});

  const onLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    const containerMaxHeight = img.parentElement
      ? img.parentElement.clientHeight - siblingsHeight
      : 0;

    const containerMaxWidth = img.parentElement?.clientWidth || 0;

    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;

    // Check if image fits within container
    if (
      naturalWidth <= containerMaxWidth &&
      naturalHeight <= containerMaxHeight
    ) {
      // Small image - use exact dimensions
      setDimensions({ width: naturalWidth, height: naturalHeight });
    } else {
      // Large image - let CSS max-width/max-height handle scaling
      setDimensions({});
    }
  };

  return (
    <PreviewImage src={preview} style={{ ...dimensions }} onLoad={onLoad} />
  );
};

const PreviewVideoComponent = ({ preview }: { preview: string }) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });
  return (
    <PreviewVideo controls preload="none" src={preview}>
      {tNs("message.video.notSupported")}
    </PreviewVideo>
  );
};

const PreviewAudioComponent = ({ preview }: { preview: string }) => {
  return (
    <PreviewAudio>
      <audio src={preview} controls autoPlay={false} />
    </PreviewAudio>
  );
};

const PreviewDocumentComponent = ({
  iconPath,
  fileName,
  fileSize,
}: {
  iconPath?: string;
  fileName?: string;
  fileSize: number;
}) => {
  let fileSizeInKB = (fileSize / 1024).toFixed(2) + " KB";
  return (
    <PreviewDoc>
      <DocImg src={iconPath} alt={fileName} />
      <Box sx={{ flexDirection: "column" }}>
        <FileDetail fileAttribute={"File name:"} value={fileName} />
        <FileDetail fileAttribute={"Size:"} value={fileSizeInKB} />
      </Box>
    </PreviewDoc>
  );
};

const FileDetail = ({
  fileAttribute,
  value,
}: {
  fileAttribute: string;
  value: string | undefined;
}) => {
  return (
    <Box sx={{
      display: "flex"
    }}>
      <Typography sx={{ marginRight: "3px" }} variant="body1">
        {fileAttribute}
      </Typography>
      <Typography variant="body1" sx={{
        fontWeight: "700"
      }}>
        {value}
      </Typography>
    </Box>
  );
};
